import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle2, LogOut, Home } from 'lucide-react';
import { useApp } from '@/context/AppContext';

/**
 * Iter 41 — Step 5 of the bare evaluated flow.
 * Shown after the user successfully submits an evaluation. Single clear
 * confirmation + a primary "Log out" button. Users can also return to their
 * other evaluations if they have more pending.
 */
export default function EvaluatedThankYou() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { logout } = useApp();
  const isES = (i18n.language || '').startsWith('es');

  const doLogout = async () => {
    try { await logout?.(); } catch { /* ignore */ }
    navigate('/login', { replace: true });
  };

  return (
    <Card className="p-10 lg:p-14 text-center shadow-card" data-testid="evaluated-thankyou">
      <div className="h-16 w-16 rounded-full bg-success/15 text-success flex items-center justify-center mx-auto">
        <CheckCircle2 className="h-9 w-9" />
      </div>
      <h1 className="font-display text-3xl font-bold tracking-tight mt-6">
        {t('pages.evaluated.done.title', {
          defaultValue: isES ? 'Gracias, tu evaluación ha sido enviada' : 'Thank you, your evaluation has been submitted',
        })}
      </h1>
      <p className="text-sm text-muted-foreground mt-3 max-w-md mx-auto">
        {t('pages.evaluated.done.body', {
          defaultValue: isES
            ? 'Tus respuestas se han enviado correctamente. El equipo responsable continuará con el proceso de análisis y preparación de resultados. Tu organización te contactará sobre los próximos pasos.'
            : 'Your responses have been submitted successfully. Your evaluation will now enter the review process. Your organization will contact you regarding the next steps.',
        })}
      </p>
      <div className="flex flex-wrap items-center justify-center gap-3 mt-8">
        <Button
          onClick={() => navigate('/me', { replace: true })}
          className="bg-gradient-primary hover:opacity-90 gap-1.5"
          data-testid="evaluated-back-home"
        >
          <Home className="h-4 w-4" />
          {t('pages.evaluated.done.backHome', { defaultValue: isES ? 'Volver a mi panel' : 'Return to my dashboard' })}
        </Button>
        <Button variant="outline" onClick={doLogout} className="gap-1.5" data-testid="evaluated-thankyou-logout">
          <LogOut className="h-4 w-4" />
          {t('common.logout', { defaultValue: isES ? 'Cerrar sesión' : 'Log out' })}
        </Button>
      </div>
    </Card>
  );
}
